import {
  bucketDate,
  ChartGranularity,
  DashboardTimeZone,
  metricValue,
  timeFor,
  useAnalyticsOverTime,
  useAnalyticsTotals,
  type AnalyticsQuestion,
  type AnalyticsRow,
  type DashboardZone,
  type Dataset,
  type Filters,
  type Metric,
  type TimeField,
} from 'corso-admin-ui';
import {
  ClaimStatusEnum,
  CrewClaimResolutionMethodEnum,
  CrewClaimStatusCode,
  InvoiceSectionName,
  RollingDatePreset,
  type CrewMerchantUi,
  type ViewDateValue,
} from 'corso-types';
import { addDays, differenceInCalendarDays, format, subDays } from 'date-fns';
import { useInvoices } from '~/features/billing/InvoicesPage';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import { useStoreId, useStoreUser } from '~/providers/StoreUserProvider';
import { storeSource } from '../analyticsSources';
import { useFrontReports } from '../CXReportPage';
import {
  ImpactKind,
  sumOf,
  type Impact,
  type ValueHomeData,
  type ValueStream,
} from './valueHome';

export const homePresets = [
  RollingDatePreset.last7Days,
  RollingDatePreset.last30Days,
  RollingDatePreset.last90Days,
  RollingDatePreset.last12Months,
] as const;

export type HomePeriod = (typeof homePresets)[number];

const returnFilters = { claimTypes: ['Return'] } satisfies Filters<'claims'>;
// a denied item keeps the resolution it was given, so only approved ones were kept
const keptFilters = {
  ...returnFilters,
  claimStatuses: [CrewClaimStatusCode.approved],
  resolutions: [
    CrewClaimResolutionMethodEnum.variantExchange,
    CrewClaimResolutionMethodEnum.giftCard,
  ],
} satisfies Filters<'claimItems'>;
// a claim that mixes Shop Now with other lines counts whole, as on the Shop Now report
const shopNowFilters = {
  ...returnFilters,
  hasShopNow: true,
} satisfies Filters<'claims'>;
const madeRightFilters = {
  statuses: [ClaimStatusEnum.closedReordered, ClaimStatusEnum.closedRefunded],
} satisfies Filters<'shippingClaims'>;

const pointsOf = (query: ReturnType<typeof useAnalyticsOverTime>) =>
  query.status === 'success' ? query.data.points : [];
const total = <D extends Dataset>(
  dataset: D,
  metric: Metric<D>,
  row: AnalyticsRow | undefined,
) => metricValue(dataset, metric, row) ?? 0;
const zip = (
  a: number[],
  b: number[],
  combine: (x: number, y: number) => number,
) => a.map((value, index) => combine(value, b[index] ?? 0));
// retained and new revenue already hold every fee: one taken from a refund was retained, one paid by card was new
const keptIn = (row: AnalyticsRow | undefined) =>
  total('claims', 'retainedRevenue', row) +
  total('claims', 'newRevenue', row) -
  total('claims', 'totalFees', row);

// the part of an invoice's or report's period that falls in [from, to)
const shareWithin = (
  { fromDate, toDate }: { fromDate: string; toDate: string },
  from: number,
  to: number,
) => {
  const start = Date.parse(fromDate);
  const end = Date.parse(toDate);
  const overlap = Math.min(end, to) - Math.max(start, from);
  return end > start && overlap > 0 ? overlap / (end - start) : 0;
};
const sectionOf = (invoice: CrewMerchantUi.Invoice, name: InvoiceSectionName) =>
  sumOf(
    invoice.sections
      .filter((section) => section.name === name)
      .map(({ amount }) => amount),
  );

/** Revenue Corso kept or brought in over the chosen period, against the same span before, read from the analytics service. */
export const useValueHome = (preset: HomePeriod) => {
  const period = { kind: 'preset', preset } as const satisfies ViewDateValue;
  const { store } = useStoreUser();
  const enabled = useEnabledClaimType();

  const source = storeSource(useStoreId(), store.currencyCode);

  const timeZone: DashboardZone = store.ianaTimezone
    ? { key: DashboardTimeZone.store, iana: store.ianaTimezone }
    : { key: DashboardTimeZone.local };

  const time = timeFor(period, timeZone);
  const range = time?.range;
  const edges = range?.bucketEdges ?? [];
  const firstDay = bucketDate(edges[0] ?? new Date().toISOString(), timeZone);

  // the same number of days, ending the day before this window starts; a long window counts in months, so its days come from its ends, in store time
  const spanDays = range
    ? differenceInCalendarDays(bucketDate(range.to, timeZone), firstDay)
    : 0;
  const previousStart = subDays(firstDay, spanDays);
  const previousPeriod = {
    kind: 'custom',
    range: {
      start: previousStart.toISOString(),
      end: subDays(firstDay, 1).toISOString(),
    },
  } satisfies ViewDateValue;

  // invoices are billed in US dollars, so they only compare with a store's revenue in them
  const isBilledInCurrency = store.currencyCode === 'USD';
  const invoices = useInvoices();
  const billed = isBilledInCurrency ? (invoices.data ?? []) : [];
  const weekly = billed.filter(({ isMonthlyCrew }) => !isMonthlyCrew);
  const windowStart = Date.parse(range?.from ?? '');
  const windowEnd = Date.parse(range?.to ?? '');
  const invoicedThrough = Math.max(
    ...weekly.map(({ toDate }) => Date.parse(toDate)),
  );
  // protection only counts the days a protection invoice has reached, and the period before is weighed over as many
  const protectionDays =
    weekly.length > 0 && range
      ? Math.max(
          0,
          differenceInCalendarDays(
            bucketDate(
              new Date(Math.min(invoicedThrough, windowEnd)).toISOString(),
              timeZone,
            ),
            firstDay,
          ),
        )
      : 0;
  const previousProtectionPeriod = {
    kind: 'custom',
    range: {
      start: previousStart.toISOString(),
      end: addDays(previousStart, protectionDays - 1).toISOString(),
    },
  } satisfies ViewDateValue;

  const ask = <D extends Dataset>(
    dataset: D,
    timeField: TimeField<D>,
    filters: Filters<D>,
    metrics: readonly [Metric<D>, ...Metric<D>[]],
    date: ViewDateValue = period,
  ) =>
    ({
      scope: { source, dataset, date, timeZone, timeField, filters },
      metrics,
      dimensions: [],
    }) satisfies AnalyticsQuestion<D>;

  const returns = enabled.isReturnsEnabled;
  const protection = enabled.isCheckoutPlusEnabled;
  const editing = enabled.isOrderEditingEnabled;
  const registering = enabled.isRegistrationEnabled;
  const servicing = enabled.isWarrantyEnabled;
  const isProtectionCounted = protection && protectionDays > 0;

  const claimMetrics = ['retainedRevenue', 'newRevenue', 'totalFees'] as const;

  const claims = useAnalyticsOverTime(
    ask('claims', 'finalizedOn', returnFilters, claimMetrics),
    undefined,
    returns,
  );

  const previousClaims = useAnalyticsTotals(
    returns
      ? ask(
          'claims',
          'finalizedOn',
          returnFilters,
          claimMetrics,
          previousPeriod,
        )
      : undefined,
  );

  const shopNow = useAnalyticsOverTime(
    ask('claims', 'finalizedOn', shopNowFilters, claimMetrics),
    undefined,
    returns,
  );

  const previousShopNow = useAnalyticsTotals(
    returns
      ? ask(
          'claims',
          'finalizedOn',
          shopNowFilters,
          claimMetrics,
          previousPeriod,
        )
      : undefined,
  );

  const kept = useAnalyticsTotals(
    returns
      ? ask('claimItems', 'finalizedOn', keptFilters, ['claims'])
      : undefined,
  );

  const editMetrics = ['newRevenue', 'minutesSaved'] as const;

  const edits = useAnalyticsOverTime(
    ask('orderEdits', 'createdOn', {}, editMetrics),
    undefined,
    editing,
  );

  const previousEdits = useAnalyticsTotals(
    editing
      ? ask('orderEdits', 'createdOn', {}, editMetrics, previousPeriod)
      : undefined,
  );

  const protectedOrders = useAnalyticsOverTime(
    ask('orders', 'orderedOn', { isProtected: true }, ['protectionPrice']),
    undefined,
    isProtectionCounted,
  );

  const madeRight = useAnalyticsTotals(
    protection
      ? ask('shippingClaims', 'resolvedOn', madeRightFilters, ['claims'])
      : undefined,
  );

  const registrationMetrics = ['newSubscribers', 'newCustomers'] as const;

  const registrations = useAnalyticsTotals(
    registering
      ? ask('registrations', 'createdOn', {}, registrationMetrics)
      : undefined,
  );

  const warranties = useAnalyticsTotals(
    servicing
      ? ask('claims', 'finalizedOn', { claimTypes: ['Warranty'] }, ['claims'])
      : undefined,
  );

  // monthly reports on the support Corso handles for the store, as on the Customer Success page
  const supportReports = useFrontReports();

  const overTime = [
    returns && claims,
    returns && shopNow,
    editing && edits,
    isProtectionCounted && protectedOrders,
  ].filter((query) => query !== false);

  const totals = [
    returns && previousClaims,
    returns && previousShopNow,
    returns && kept,
    editing && previousEdits,
    protection && madeRight,
    registering && registrations,
    servicing && warranties,
    invoices,
    supportReports,
  ].filter((query) => query !== false);

  const isError =
    enabled.isError ||
    overTime.some(({ status }) => status === 'error') ||
    totals.some((query) => query.isError);

  const isPending =
    !enabled.hasLoaded ||
    overTime.some(({ status }) => status === 'pending') ||
    totals.some((query) => query.isPending);

  if (isError) return { status: 'error' } as const;

  if (isPending) return { status: 'pending' } as const;

  const daily = <D extends Dataset>(
    dataset: D,
    metric: Metric<D>,
    points: readonly { row: AnalyticsRow | undefined }[],
  ) =>
    edges.map(
      (_, index) => metricValue(dataset, metric, points[index]?.row) ?? 0,
    );

  const claimPoints = pointsOf(claims);
  const shopNowPoints = pointsOf(shopNow);
  const editPoints = pointsOf(edits);

  const keptOf = (points: readonly { row: AnalyticsRow | undefined }[]) =>
    edges.map((_, index) => keptIn(points[index]?.row));

  const allKept = keptOf(claimPoints);
  const shopNowKept = keptOf(shopNowPoints);
  const exchangeKept = zip(allKept, shopNowKept, (x, y) => x - y);
  const fees = daily('claims', 'totalFees', claimPoints);
  const editRevenue = daily('orderEdits', 'newRevenue', editPoints);
  const minutesSaved =
    edits.status === 'success'
      ? total('orderEdits', 'minutesSaved', edits.data.totals)
      : 0;
  const returnsKept = total('claimItems', 'claims', kept.data);

  const dayBounds = edges.map((edge, index) => ({
    from: Date.parse(edge),
    to: Date.parse(edges[index + 1] ?? range?.to ?? ''),
  }));
  const invoicedWithin = (
    names: readonly InvoiceSectionName[],
    from: number,
    to: number,
  ) =>
    sumOf(
      weekly.flatMap((invoice) =>
        names.map(
          (name) => sectionOf(invoice, name) * shareWithin(invoice, from, to),
        ),
      ),
    );

  // Checkout Plus is the protected order value its invoices billed; each invoice's part of the window is spread over its days by that day's protected orders, so the line moves with them
  const protectedPerDay = daily(
    'orders',
    'protectionPrice',
    pointsOf(protectedOrders),
  );
  const spreadOf = (start: number, end: number) => {
    const covered = dayBounds.map(({ from, to }) =>
      to > from
        ? Math.max(0, Math.min(to, end) - Math.max(from, start)) / (to - from)
        : 0,
    );
    const weights = covered.map(
      (share, index) => share * (protectedPerDay[index] ?? 0),
    );
    // an invoice with no protected orders recorded on its days falls back to an even spread
    const spread = sumOf(weights) > 0 ? weights : covered;
    const whole = sumOf(spread);
    return spread.map((weight) => (whole > 0 ? weight / whole : 0));
  };
  const protectedOrderValue = weekly.reduce(
    (sums, invoice) => {
      const amount =
        sectionOf(invoice, InvoiceSectionName.protectedOrders) *
        shareWithin(invoice, windowStart, windowEnd);
      const spread = spreadOf(
        Date.parse(invoice.fromDate),
        Date.parse(invoice.toDate),
      );
      return sums.map((sum, index) => sum + amount * (spread[index] ?? 0));
    },
    dayBounds.map(() => 0),
  );
  const previousProtectionRange = timeFor(
    previousProtectionPeriod,
    timeZone,
  )?.range;
  const claimTotals =
    claims.status === 'success' ? claims.data.totals : undefined;
  const shopNowTotals =
    shopNow.status === 'success' ? shopNow.data.totals : undefined;
  const editTotals = edits.status === 'success' ? edits.data.totals : undefined;

  const streams = (
    [
      returns && {
        key: 'retained',
        daily: exchangeKept,
        value: keptIn(claimTotals) - keptIn(shopNowTotals),
        previous: keptIn(previousClaims.data) - keptIn(previousShopNow.data),
      },
      returns && {
        key: 'shopNow',
        daily: shopNowKept,
        value: keptIn(shopNowTotals),
        previous: keptIn(previousShopNow.data),
      },
      returns && {
        key: 'recovered',
        daily: fees,
        value: total('claims', 'totalFees', claimTotals),
        previous: total('claims', 'totalFees', previousClaims.data),
      },
      editing && {
        key: 'orderEdits',
        daily: editRevenue,
        value: total('orderEdits', 'newRevenue', editTotals),
        previous: total('orderEdits', 'newRevenue', previousEdits.data),
      },
      isProtectionCounted && {
        key: 'protection',
        daily: protectedOrderValue,
        value: sumOf(protectedOrderValue),
        previous: invoicedWithin(
          [InvoiceSectionName.protectedOrders],
          Date.parse(previousProtectionRange?.from ?? ''),
          Date.parse(previousProtectionRange?.to ?? ''),
        ),
      },
    ] satisfies (ValueStream | false)[]
  ).filter((stream) => stream !== false);

  // a monthly report counts for the part of its month inside the window; its rates are weighted by what they're rates of
  const supportWithin = (from: number, to: number) => {
    const reports = (supportReports.data ?? [])
      .map((report) => ({ report, share: shareWithin(report, from, to) }))
      .filter(({ share }) => share > 0);
    const weighted = (
      weightOf: (report: CrewMerchantUi.FrontReport) => number,
    ) => sumOf(reports.map(({ report, share }) => weightOf(report) * share));
    const ratings = weighted(({ metrics }) => metrics.totalCsatScoresCollected);
    return {
      conversations: weighted(({ metrics }) => metrics.newConversations),
      satisfaction:
        ratings > 0
          ? weighted(
              ({ metrics }) =>
                metrics.csatScorePercent * metrics.totalCsatScoresCollected,
            ) / ratings
          : 0,
    };
  };
  const support = supportWithin(windowStart, windowEnd);

  // a measure with nothing behind it gets no story
  const impacts = (
    [
      returns && {
        kind: ImpactKind.customersKept,
        value: returnsKept,
      },
      editing && {
        kind: ImpactKind.hoursSaved,
        value: minutesSaved / 60,
      },
      servicing && {
        kind: ImpactKind.warrantiesServiced,
        value: total('claims', 'claims', warranties.data),
      },
      protection && {
        kind: ImpactKind.ordersMadeRight,
        value: total('shippingClaims', 'claims', madeRight.data),
      },
      registering && {
        kind: ImpactKind.newSubscribers,
        value: total('registrations', 'newSubscribers', registrations.data),
      },
      registering && {
        kind: ImpactKind.newCustomers,
        value: total('registrations', 'newCustomers', registrations.data),
      },
      {
        kind: ImpactKind.conversations,
        value: support.conversations,
      },
      {
        kind: ImpactKind.satisfaction,
        value: support.satisfaction,
      },
    ] satisfies (Impact | false)[]
  )
    .filter((impact) => impact !== false)
    .filter(({ value }) => value > 0);

  // what Corso kept: monthly Crew invoices, and weekly protection invoices after the claims and revenue share they credited back
  const planCost = sumOf(
    billed.map(
      (invoice) =>
        invoice.totalAmount * shareWithin(invoice, windowStart, windowEnd),
    ),
  );

  const data = {
    days: edges.map((edge) => format(bucketDate(edge, timeZone), 'yyyy-MM-dd')),
    preset,
    granularity: time?.granularity ?? ChartGranularity.day,
    streams,
    impacts,
    planCost: planCost > 0 ? planCost : null,
  } satisfies ValueHomeData;

  return { status: 'success', data } as const;
};
